import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CustomerLayout } from "@/components/CustomerLayout";
import { fmtAddress } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LogOut } from "lucide-react";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const a = user.address || {};
  const [f, setF] = useState({
    name: user.name || "", phone: user.phone || "",
    block: a.block || "", lot: a.lot || "", phase: a.phase || "", street: a.street || "",
    landmark: a.landmark || "", notes: a.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/me", {
        name: f.name, phone: f.phone,
        address: { block: f.block, lot: f.lot, phase: f.phase, street: f.street, landmark: f.landmark, notes: f.notes,
          subdivision: "Canyon Ranch Community", barangay: "Lantic", city: "Carmona", province: "Cavite" },
      });
      await refresh();
      toast.success("Profile updated.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <CustomerLayout>
      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-3xl font-bold">My Account</h1>
          <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn"><LogOut size={16} className="mr-1" /> Log out</Button>
        </div>
        <div className="bg-white rounded-2xl border p-5 space-y-4" data-testid="account-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" data-testid="acc-name" /></div>
            <div><Label>Mobile</Label><Input value={f.phone} onChange={set("phone")} className="mt-1" data-testid="acc-phone" /></div>
          </div>
          <div><Label>Email</Label><Input value={user.email} disabled className="mt-1 bg-muted" /></div>
          <div className="rounded-xl bg-[#FAF8F5] border p-4">
            <div className="text-sm font-semibold text-[#1B5E20] mb-3">Delivery Address · Canyon Ranch, Carmona, Cavite</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Block</Label><Input value={f.block} onChange={set("block")} className="mt-1" data-testid="acc-block" /></div>
              <div><Label className="text-xs">Lot</Label><Input value={f.lot} onChange={set("lot")} className="mt-1" data-testid="acc-lot" /></div>
              <div><Label className="text-xs">Phase</Label><Input value={f.phase} onChange={set("phase")} className="mt-1" /></div>
              <div><Label className="text-xs">Street</Label><Input value={f.street} onChange={set("street")} className="mt-1" data-testid="acc-street" /></div>
            </div>
            <div className="mt-3"><Label className="text-xs">Landmark</Label><Input value={f.landmark} onChange={set("landmark")} className="mt-1" /></div>
            <div className="mt-3"><Label className="text-xs">Delivery Notes</Label><Textarea value={f.notes} onChange={set("notes")} rows={2} className="mt-1" /></div>
            {user.address && <p className="text-xs text-muted-foreground mt-3">Current: {fmtAddress(user.address)}</p>}
          </div>
          <Button onClick={save} disabled={busy} className="w-full h-11 bg-[#1B5E20] hover:bg-[#144718]" data-testid="save-account-btn">{busy ? "Saving…" : "Save Changes"}</Button>
        </div>
      </div>
    </CustomerLayout>
  );
}
